import { useState, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { Loader2, ClipboardCheck, MessageSquare } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Page } from '@/components/layout/Page';
import { PageHeader } from '@/components/layout/PageHeader';
import { PageContent } from '@/components/layout/PageContent';
import { SubmitResults, type SubmitResultsData } from '@/features/races/SubmitResults';
import { GET_RACE_DETAILS } from '@/graphql/queries';
import { CLOSE_RACE, SUBMIT_RACE_RESULTS } from '@/graphql/mutations';
import { Gender } from '@/gql/graphql';
import { useToast } from '@/components/ui/Toast';
import { extractErrorCode } from '@/lib/graphql-errors';

export function RaceResultsPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation('races');
    const { t: tb } = useTranslation('bets');
    const { t: tc } = useTranslation();
    const { toast } = useToast();

    const [activeGender, setActiveGender] = useState<Gender>(Gender.Male);
    const [showClosingStep, setShowClosingStep] = useState(false);
    const [closingMessage, setClosingMessage] = useState('');
    const [isClosing, setIsClosing] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [maleUnplaced, setMaleUnplaced] = useState(0);
    const [femaleUnplaced, setFemaleUnplaced] = useState(0);
    const maleDataRef = useRef<(() => SubmitResultsData) | null>(null);
    const femaleDataRef = useRef<(() => SubmitResultsData) | null>(null);

    const { data, loading } = useQuery(GET_RACE_DETAILS, {
        variables: { id: id! },
        skip: !id,
    });

    const [closeRace] = useMutation(CLOSE_RACE, { refetchQueries: ['GetRaceDetails'] });
    const [submitResults] = useMutation(SUBMIT_RACE_RESULTS, { refetchQueries: ['GetRaceDetails'] });

    const race = data?.race;

    const availableGenders = useMemo(() => {
        const runners = race?.runners ?? [];
        const genders: Gender[] = [];
        if (runners.some(r => r.gender === Gender.Male)) genders.push(Gender.Male);
        if (runners.some(r => r.gender === Gender.Female)) genders.push(Gender.Female);
        return genders;
    }, [race?.runners]);

    const isMultiGender = availableGenders.length > 1;
    const effectiveGender = availableGenders.includes(activeGender)
        ? activeGender : availableGenders[0] ?? Gender.Male;

    const handleMaleDataRef = useCallback((fn: (() => SubmitResultsData) | null) => { maleDataRef.current = fn; }, []);
    const handleFemaleDataRef = useCallback((fn: (() => SubmitResultsData) | null) => { femaleDataRef.current = fn; }, []);
    const handleMaleUnplaced = useCallback((count: number) => setMaleUnplaced(count), []);
    const handleFemaleUnplaced = useCallback((count: number) => setFemaleUnplaced(count), []);

    const totalUnplaced = maleUnplaced + femaleUnplaced;
    const canSubmit = totalUnplaced === 0 && !isSubmitting;

    const handleSubmit = useCallback(async () => {
        if (!id || totalUnplaced > 0) return;
        setIsSubmitting(true);
        try {
            const maleData = maleDataRef.current?.() ?? { results: [], dnsRunnerIds: [] };
            const femaleData = femaleDataRef.current?.() ?? { results: [], dnsRunnerIds: [] };
            const allResults = [...maleData.results, ...femaleData.results];
            const allDns = [...maleData.dnsRunnerIds, ...femaleData.dnsRunnerIds];

            await submitResults({
                variables: { submitRaceResultsInput: { raceId: id, results: allResults, dnsRunnerIds: allDns } },
            });
            toast(t('results.success'), 'success');
            setShowClosingStep(true);
        } catch (err: unknown) {
            const code = extractErrorCode(err);
            toast(tc(`error.${code}`, { defaultValue: t('results.error') }), 'error');
        } finally { setIsSubmitting(false); }
    }, [id, totalUnplaced, submitResults, toast, t, tc]);

    const handleCloseRace = async (message?: string) => {
        if (!id) return;
        setIsClosing(true);
        try {
            await closeRace({ variables: { raceId: id, closingMessage: message?.trim() || null } });
            navigate(`/races/${id}/recap`, { replace: true });
        } catch {
            toast(tc('error.generic'), 'error');
            navigate(`/races/${id}`, { replace: true });
        } finally { setIsClosing(false); }
    };

    if (!id) return null;

    if (loading || isClosing) return (
        <Page>
            <PageHeader title={t('results.title')} backLink={`/races/${id}`} width="standard" />
            <div className="flex items-center justify-center py-20">
                <Loader2 size={28} className="animate-spin text-emerald-600" />
            </div>
        </Page>
    );

    // Closing step
    if (showClosingStep) {
        return (
            <Page>
                <PageHeader title={t('results.title')} backLink={`/races/${id}`} width="standard" />
                <PageContent width="standard" className="space-y-6 py-8">
                    <div className="text-center space-y-2">
                        <div className="w-12 h-12 bg-amber-50 rounded-full flex items-center justify-center mx-auto">
                            <MessageSquare size={24} className="text-amber-500" />
                        </div>
                        <h2 className="text-lg font-bold text-slate-900">{t('results.closingStep.title')}</h2>
                        <p className="text-sm text-slate-500">{t('results.closingStep.subtitle')}</p>
                    </div>
                    <textarea
                        value={closingMessage}
                        onChange={(e) => setClosingMessage(e.target.value)}
                        placeholder={t('results.closingStep.placeholder')}
                        maxLength={2000}
                        className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none transition-all font-medium placeholder:text-slate-400 text-slate-900 min-h-[120px] resize-y"
                    />
                    <p className="text-xs text-slate-400 text-right">{closingMessage.length} / 2000</p>
                    <div className="flex flex-col gap-3">
                        <button onClick={() => handleCloseRace(closingMessage)} className="w-full bg-emerald-600 text-white h-14 rounded-2xl font-bold text-lg flex items-center justify-center gap-2 active:scale-95 transition-transform hover:bg-emerald-700">
                            {t('results.closingStep.close')}
                        </button>
                        <button onClick={() => handleCloseRace()} className="text-sm font-medium text-slate-400 hover:text-slate-600 transition-colors py-2">
                            {t('results.closingStep.skip')}
                        </button>
                    </div>
                </PageContent>
            </Page>
        );
    }

    const headerActions = (
        <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={cn(
                "flex items-center gap-1.5 px-3 py-2 rounded-xl font-bold text-xs transition-all active:scale-95",
                canSubmit
                    ? "bg-emerald-600 text-white shadow-md shadow-emerald-200 hover:bg-emerald-700"
                    : "bg-slate-200 text-slate-400 cursor-not-allowed"
            )}
        >
            <ClipboardCheck size={14} />
            {tb('submitShort')}
        </button>
    );

    return (
        <Page>
            <PageHeader
                title={t('results.title')}
                backLink={`/races/${id}`}
                width="standard"
                actions={headerActions}
            />

            {/* Gender tabs */}
            {isMultiGender && (
                <PageContent width="standard" className="pt-2 pb-0">
                    <div className="flex gap-1 bg-slate-100 rounded-xl p-1" role="tablist">
                        {availableGenders.map((g) => {
                            const isActive = g === effectiveGender;
                            const label = g === Gender.Male ? tb('gender.MALE') : tb('gender.FEMALE');
                            return (
                                <button
                                    key={g}
                                    role="tab"
                                    aria-selected={isActive}
                                    onClick={() => setActiveGender(g)}
                                    className={cn(
                                        "flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold transition-all",
                                        isActive ? "bg-white shadow-sm text-slate-900" : "text-slate-400 hover:text-slate-600"
                                    )}
                                >
                                    <span className={cn("w-2 h-2 rounded-full", g === Gender.Male ? "bg-blue-500" : "bg-pink-500")} />
                                    {label}
                                </button>
                            );
                        })}
                    </div>
                </PageContent>
            )}

            {availableGenders.map((g) => (
                <div key={g} className={g !== effectiveGender ? 'hidden' : undefined}>
                    <SubmitResults
                        raceId={id}
                        gender={g}
                        onDataRef={g === Gender.Male ? handleMaleDataRef : handleFemaleDataRef}
                        onUnplacedChange={g === Gender.Male ? handleMaleUnplaced : handleFemaleUnplaced}
                    />
                </div>
            ))}
        </Page>
    );
}
